import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../../services/api';
import {
  Mail, Phone, CheckCircle, Circle, MessageSquare,
  Search, ChevronDown, Clock, Eye, Download, Upload, X, Trash2, Tag,
  RefreshCw, LayoutList
} from 'lucide-react';
import toast from 'react-hot-toast';

interface ContactLead {
  id: number;
  name: string;
  email: string | null;
  mobile_number: string;
  subject: string;
  message: string;
  is_read: number;
  created_at: string;
  sender_user_id: number | null;
  sender_name: string | null;
  sender_email: string | null;
  platform: string | null;
  location: string | null;
}

type LeadStatus = 'contacted' | 'follow_up' | 'converted' | 'lost';

const getStoredStatus = (leadId: number): LeadStatus | null => {
  const stored = localStorage.getItem(`lead_status_${leadId}`);
  if (stored === 'contacted' || stored === 'follow_up' || stored === 'converted' || stored === 'lost') {
    return stored;
  }
  return null;
};

const setStoredStatus = (leadId: number, status: LeadStatus | null): void => {
  if (status) {
    localStorage.setItem(`lead_status_${leadId}`, status);
  } else {
    localStorage.removeItem(`lead_status_${leadId}`);
  }
};

const getLeadStatus = (lead: ContactLead): LeadStatus | null => {
  return getStoredStatus(lead.id);
};

const statusConfig: Record<string, { label: string; color: string; icon: typeof Circle; iconColor: string; bgSoft: string }> = {
  contacted: {
    label: 'Contacted',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    bgSoft: 'bg-blue-50 hover:bg-blue-100',
    icon: Phone,
    iconColor: 'text-blue-500'
  },
  follow_up: {
    label: 'Follow-up',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    bgSoft: 'bg-yellow-50 hover:bg-yellow-100',
    icon: Clock,
    iconColor: 'text-yellow-500'
  },
  converted: {
    label: 'Converted',
    color: 'bg-green-100 text-green-700 border-green-200',
    bgSoft: 'bg-green-50 hover:bg-green-100',
    icon: CheckCircle,
    iconColor: 'text-green-500'
  },
  lost: {
    label: 'Lost',
    color: 'bg-red-100 text-red-700 border-red-200',
    bgSoft: 'bg-red-50 hover:bg-red-100',
    icon: Circle,
    iconColor: 'text-red-500'
  }
};

const unsetStatusConfig = {
  label: 'Unread',
  color: 'bg-gray-100 text-gray-700 border-gray-200',
  bgSoft: 'bg-gray-50 hover:bg-gray-100',
  icon: Circle,
  iconColor: 'text-gray-400'
};

const toWhatsAppUrl = (mobile: string): string => {
  const digits = mobile.replace(/[^\d+]/g, '');
  return `https://wa.me/${digits}`;
};

// --- CSV helpers ---
function parseCSVLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') { current += '"'; i++; } else { inQuotes = false; }
      } else { current += ch; }
    } else {
      if (ch === '"') { inQuotes = true; } else if (ch === ',') { fields.push(current); current = ''; } else { current += ch; }
    }
  }
  fields.push(current);
  return fields;
}

function parseCSV(text: string): string[][] {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim() !== '');
  return lines.map(parseCSVLine);
}

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return '"' + value.replace(/"/g, '""') + '"';
  }
  return value;
}

const LEAD_IMPORT_FIELDS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'email', label: 'Email', required: false },
  { key: 'mobile_number', label: 'Mobile Number', required: true },
  { key: 'subject', label: 'Subject', required: true },
  { key: 'message', label: 'Message', required: true },
  { key: 'platform', label: 'Platform', required: false },
  { key: 'location', label: 'Location', required: false },
] as const;

// --- Import Modal Component ---
const ImportModal = ({ csvHeaders, csvRows, onConfirm, onCancel, importing }: any) => {
    // Basic structural copy for import functionality
    const [mapping, setMapping] = useState<Record<string, number>>({});
    useEffect(() => {
        const initialMap: Record<string, number> = {};
        const headerLower = csvHeaders.map((h: string) => h.toLowerCase().trim());
        for (const field of LEAD_IMPORT_FIELDS) {
        let idx = headerLower.indexOf(field.key);
        if (idx === -1) idx = headerLower.indexOf(field.label.toLowerCase());
        if (idx === -1) {
            if (field.key === 'mobile_number') idx = headerLower.findIndex((h:string) => h.includes('mobile') || h.includes('phone') || h.includes('tel'));
            else if (field.key === 'name') idx = headerLower.findIndex((h:string) => h.includes('name') && !h.includes('email'));
            else if (field.key === 'email') idx = headerLower.findIndex((h:string) => h.includes('email') || h.includes('mail'));
            else if (field.key === 'subject') idx = headerLower.findIndex((h:string) => h.includes('subject') || h.includes('topic'));
            else if (field.key === 'message') idx = headerLower.findIndex((h:string) => h.includes('message') || h.includes('body'));
        }
        initialMap[field.key] = idx >= 0 ? idx : -1;
        }
        setMapping(initialMap);
    }, [csvHeaders]);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900">Import CSV - Map Columns</h2>
                    <button onClick={onCancel} className="text-gray-400 hover:bg-gray-50 p-2 rounded-full"><X className="h-5 w-5" /></button>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    <div className="space-y-4">
                        {LEAD_IMPORT_FIELDS.map(f => (
                            <div key={f.key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                <label className="w-40 text-sm font-medium text-gray-700 flex-shrink-0">
                                    {f.label}{f.required && <span className="text-red-500 ml-1">*</span>}
                                </label>
                                <select
                                    value={mapping[f.key] ?? -1}
                                    onChange={(e) => setMapping(p => ({ ...p, [f.key]: parseInt(e.target.value) }))}
                                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                                >
                                    <option value={-1}>-- Don't import --</option>
                                    {csvHeaders.map((h:any, i:any) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                                </select>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
                    <button onClick={onCancel} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-all shadow-sm">Cancel</button>
                    <button onClick={() => onConfirm(mapping)} disabled={importing} className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all shadow-sm disabled:opacity-50 flex items-center gap-2">
                        {importing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import {csvRows.length} Rows
                    </button>
                </div>
            </div>
        </div>
    );
};

const Leads: React.FC = () => {
  const [leads, setLeads] = useState<ContactLead[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Design state
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'unset' | LeadStatus>('all');
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [leadStatuses, setLeadStatuses] = useState<Record<number, LeadStatus | null>>({});

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importHeaders, setImportHeaders] = useState<string[]>([]);
  const [importRows, setImportRows] = useState<string[][]>([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { fetchLeads(); }, []);

  useEffect(() => {
    const statuses: Record<number, LeadStatus | null> = {};
    leads.forEach(lead => { statuses[lead.id] = getLeadStatus(lead); });
    setLeadStatuses(statuses);
  }, [leads]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contact-leads');
      if (response.data.success) {
        setLeads(response.data.leads || []);
      } else toast.error('Failed to load leads');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = (leadId: number, newStatus: LeadStatus) => {
    setStoredStatus(leadId, newStatus);
    setLeadStatuses(prev => ({ ...prev, [leadId]: newStatus }));
    const config = statusConfig[newStatus];
    toast.success(`Status updated to ${config?.label || newStatus}`);
  };

  const handleDeleteLead = async (leadId: number, leadName: string) => {
    if (!window.confirm(`Delete lead "${leadName}"? This cannot be undone.`)) return;
    try {
      const response = await api.delete(`/contact-leads/${leadId}`);
      if (response.data.success) {
        setLeads(prev => prev.filter(l => l.id !== leadId));
        setSelectedIds(prev => { const next = new Set(prev); next.delete(leadId); return next; });
        setStoredStatus(leadId, null);
        setLeadStatuses(prev => { const next = { ...prev }; delete next[leadId]; return next; });
        if (selectedLeadId === leadId) setSelectedLeadId(null);
        toast.success('Lead deleted');
      } else toast.error(response.data.message || 'Failed to delete lead');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete lead');
    }
  };

  const uniqueSubjects = useMemo(() => Array.from(new Set(leads.map(l => l.subject))).sort(), [leads]);
  const predefinedSubjects = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('contact_form_subjects') || '[]'); } catch { return []; }
  }, []);
  const allSubjects = useMemo(() => Array.from(new Set([...uniqueSubjects, ...predefinedSubjects])).sort(), [uniqueSubjects, predefinedSubjects]);

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!lead.name.toLowerCase().includes(q) && !(lead.email?.toLowerCase().includes(q)) && !lead.subject.toLowerCase().includes(q) && !lead.message.toLowerCase().includes(q) && !lead.mobile_number.includes(q)) return false;
      }
      if (statusFilter !== 'all') {
        const status = leadStatuses[lead.id] ?? getLeadStatus(lead);
        if (statusFilter === 'unset') { if (status !== null) return false; }
        else if (status !== statusFilter) return false;
      }
      if (subjectFilter !== 'all' && lead.subject !== subjectFilter) return false;
      return true;
    });
  }, [leads, searchQuery, statusFilter, subjectFilter, leadStatuses]);

  const formatDate = (ds: string) => new Date(ds).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const formatTimeAgo = (ds: string) => {
      const ms = Date.now() - new Date(ds).getTime();
      const m = Math.floor(ms/60000); if (m < 60) return `${m}m ago`;
      const h = Math.floor(m/60); if (h < 24) return `${h}h ago`;
      const d = Math.floor(h/24); return `${d}d ago`;
  };

  const toggleSelectAll = () => {
    if (filteredLeads.length > 0 && filteredLeads.every(l => selectedIds.has(l.id))) {
      setSelectedIds(prev => { const next = new Set(prev); filteredLeads.forEach(l => next.delete(l.id)); return next; });
    } else {
      setSelectedIds(prev => { const next = new Set(prev); filteredLeads.forEach(l => next.add(l.id)); return next; });
    }
  };

  const handleExport = () => {
    const toExport = selectedIds.size > 0 ? filteredLeads.filter(l => selectedIds.has(l.id)) : filteredLeads;
    if (!toExport.length) return toast.error('No leads to export');
    const headers = ['Name', 'Email', 'Mobile', 'Subject', 'Message', 'Platform', 'Location', 'Status', 'Created At'];
    const rows = toExport.map(l => {
      const st = leadStatuses[l.id] ?? getLeadStatus(l);
      return [csvEscape(l.name), csvEscape(l.email||''), csvEscape(l.mobile_number), csvEscape(l.subject), csvEscape(l.message), csvEscape(l.platform||''), csvEscape(l.location||''), csvEscape(st && statusConfig[st] ? statusConfig[st].label : ''), csvEscape(formatDate(l.created_at))].join(',');
    });
    const blob = new Blob([[headers.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = u; a.download = `leads-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(u); toast.success(`Exported ${toExport.length} leads`);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      if (!text) return toast.error('Could not read file');
      const rows = parseCSV(text);
      if (rows.length < 2) return toast.error('CSV must have a header row and at least one data row');
      setImportHeaders(rows[0]); setImportRows(rows.slice(1)); setImportModalOpen(true);
    };
    reader.readAsText(file); e.target.value = '';
  };

  const handleImportConfirm = async (mapping: Record<string, number>) => {
    for (const f of LEAD_IMPORT_FIELDS) if (f.required && (mapping[f.key] === undefined || mapping[f.key] < 0)) return toast.error(`"${f.label}" is required.`);
    setImporting(true);
    try {
      const res = await api.post('/contact-leads/import', { rows: importRows, columnMapping: mapping });
      if (res.data.success) {
        toast.success(`Imported ${res.data.imported} leads${res.data.skipped > 0 ? ` (${res.data.skipped} skipped)` : ''}`);
        setImportModalOpen(false); setImportHeaders([]); setImportRows([]); fetchLeads();
      } else toast.error(res.data.message || 'Import failed');
    } catch (e:any) { toast.error(e.response?.data?.message || 'Import failed'); } finally { setImporting(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const selectedLead = filteredLeads.find(l => l.id === selectedLeadId);
  const isAllSelected = filteredLeads.length > 0 && filteredLeads.every(l => selectedIds.has(l.id));

  // Active filters count
  let activeFiltersCount = 0;
  if (statusFilter !== 'all') activeFiltersCount++;
  if (subjectFilter !== 'all') activeFiltersCount++;
  if (searchQuery) activeFiltersCount++;

  return (
    <div className="bg-[#FAFAFA] min-h-[calc(100vh-4rem)] p-4 sm:p-6 lg:p-8 font-inter">
      <div className="max-w-[1600px] mx-auto space-y-6">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Leads Management</h1>
            <p className="text-sm text-gray-500 mt-1 sm:mt-2">Keep track of your prospects, follow-ups, and converted clients.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileSelect} />
            <div className="flex shadow-sm rounded-xl border border-gray-200 bg-white overflow-hidden">
                <button
                onClick={() => {
                    const demoContent = "Name,Email,Mobile Number,Subject,Message,Platform,Location\nJohn Doe,john@example.com,+1234567890,Service Inquiry,I need help with this service.,Website,New York";
                    const blob = new Blob([demoContent], { type: 'text/csv;charset=utf-8;' });
                    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'Demo_Leads.csv'; a.click();
                }}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors border-r border-gray-200"
                >
                Demo CSV
                </button>
                <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors border-r border-gray-200">
                <Upload className="h-4 w-4" /> Import
                </button>
                <button onClick={handleExport} disabled={!filteredLeads.length} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50">
                <Download className="h-4 w-4" /> Export {selectedIds.size > 0 && `(${selectedIds.size})`}
                </button>
            </div>
          </div>
        </div>

        {/* Metrics Bar */}
        <div className="flex flex-nowrap overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:pb-0 hide-scrollbar gap-3 md:grid md:grid-cols-6 custom-scrollbar">
          {[
            { id: 'all', label: 'Total', count: leads.length, color: 'text-gray-900', border: 'border-gray-200' },
            { id: 'unset', label: 'Unread', count: leads.filter(l => l.is_read === 0).length, color: 'text-gray-900', border: 'border-gray-200' },
            { id: 'follow_up', label: 'Follow-up', count: Object.values(leadStatuses).filter(s => s === 'follow_up').length, color: 'text-yellow-600', border: 'border-yellow-200' },
            { id: 'contacted', label: 'Contacted', count: Object.values(leadStatuses).filter(s => s === 'contacted').length, color: 'text-blue-600', border: 'border-blue-200' },
            { id: 'converted', label: 'Converted', count: Object.values(leadStatuses).filter(s => s === 'converted').length, color: 'text-green-600', border: 'border-green-200' },
            { id: 'lost', label: 'Lost', count: Object.values(leadStatuses).filter(s => s === 'lost').length, color: 'text-red-600', border: 'border-red-200' },
          ].map((metric) => (
            <button
              key={metric.id}
              onClick={() => setStatusFilter(metric.id as any)}
              className={`min-w-[130px] md:min-w-0 flex flex-col items-start p-4 bg-white rounded-2xl border-2 transition-all shadow-sm
                ${statusFilter === metric.id ? `border-blue-500 bg-blue-50/30` : 'border-transparent ring-1 ring-gray-200 hover:ring-gray-300 hover:shadow-md hover:bg-gray-50'}`}
            >
              <span className={`text-2xl font-bold ${metric.color}`}>{metric.count}</span>
              <span className="text-xs font-semibold text-gray-500 mt-1 uppercase tracking-wider">{metric.label}</span>
            </button>
          ))}
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-white p-2 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex-1 w-full relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Search leads..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-transparent border-0 focus:ring-0 text-sm outline-none placeholder:text-gray-400"
                />
            </div>
            <div className="flex items-center gap-2 px-2 w-full sm:w-auto border-t sm:border-t-0 sm:border-l border-gray-100 pt-2 sm:pt-0">
                <div className="relative w-1/2 sm:w-auto">
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value as any)}
                        className="w-full appearance-none pl-4 pr-10 py-2.5 bg-gray-50 hover:bg-gray-100 border-0 rounded-xl text-sm font-medium text-gray-700 cursor-pointer outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                    >
                        <option value="all">Status</option>
                        <option value="unset">Unread</option>
                        <option value="contacted">Contacted</option>
                        <option value="follow_up">Follow-up</option>
                        <option value="converted">Converted</option>
                        <option value="lost">Lost</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                </div>
                <div className="relative w-1/2 sm:w-auto">
                    <select
                        value={subjectFilter}
                        onChange={e => setSubjectFilter(e.target.value)}
                        className="w-full appearance-none pl-4 pr-10 py-2.5 bg-gray-50 hover:bg-gray-100 border-0 rounded-xl text-sm font-medium text-gray-700 cursor-pointer outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                    >
                        <option value="all">Tags</option>
                        {allSubjects.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <Tag className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                </div>
            </div>
        </div>

        {/* Active Filters Chips */}
        {activeFiltersCount > 0 && (
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Active Filters:</span>
                {statusFilter !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        Status: <span className="capitalize">{statusFilter.replace('_', ' ')}</span>
                        <X className="h-3 w-3 cursor-pointer hover:text-gray-900" onClick={() => setStatusFilter('all')} />
                    </span>
                )}
                {subjectFilter !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        Tag: {subjectFilter}
                        <X className="h-3 w-3 cursor-pointer hover:text-gray-900" onClick={() => setSubjectFilter('all')} />
                    </span>
                )}
                {searchQuery && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                        "{searchQuery}"
                        <X className="h-3 w-3 cursor-pointer hover:text-blue-900" onClick={() => setSearchQuery('')} />
                    </span>
                )}
                <button onClick={() => { setStatusFilter('all'); setSubjectFilter('all'); setSearchQuery(''); }} className="text-xs text-gray-500 hover:text-gray-700 font-medium ml-2 transition-colors">
                    Clear All
                </button>
            </div>
        )}

        {/* --- MAIN SPLIT VIEW LAYOUT --- */}
        <div className="flex flex-col lg:flex-row gap-6 relative items-start">
            
            {/* Left Panel: List */}
            <div className={`w-full ${selectedLeadId && 'hidden lg:block'} lg:w-[50%] xl:w-[60%] flex-shrink-0 transition-all duration-300 space-y-3`}>
                
                {/* List Header */}
                <div className="flex items-center justify-between px-2 mb-2">
                    <label className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-5 h-5 rounded flex items-center justify-center border transition-all ${isAllSelected ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300 group-hover:border-blue-500'}`}>
                            {isAllSelected && <CheckCircle className="h-3.5 w-3.5 text-white" />}
                        </div>
                        <input type="checkbox" className="hidden" checked={isAllSelected} onChange={toggleSelectAll} />
                        <span className="text-sm font-medium text-gray-600">{selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select All'}</span>
                    </label>
                    <span className="text-xs text-gray-400 font-medium">{filteredLeads.length} Leads</span>
                </div>

                {/* List Items */}
                {filteredLeads.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <LayoutList className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">No leads found</h3>
                        <p className="text-gray-500 text-sm">We couldn't find any leads matching your criteria.</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filteredLeads.map(lead => {
                            const isSelected = selectedIds.has(lead.id);
                            const isActive = selectedLeadId === lead.id;
                            const status = leadStatuses[lead.id] ?? getLeadStatus(lead);
                            const config = status ? statusConfig[status] : unsetStatusConfig;

                            return (
                                <div 
                                    key={lead.id}
                                    onClick={() => setSelectedLeadId(isActive ? null : lead.id)}
                                    className={`group flex items-stretch bg-white rounded-2xl border-2 transition-all duration-200 cursor-pointer overflow-hidden
                                        ${isActive ? 'border-blue-500 bg-blue-50/20 shadow-sm' : 'border-transparent ring-1 ring-gray-200 hover:ring-gray-300 hover:shadow-md hover:bg-gray-50'}
                                    `}
                                >
                                    {/* Color strip left */}
                                    <div className={`w-1.5 flex-shrink-0 transition-colors ${status === 'contacted' ? 'bg-blue-500' : status === 'follow_up' ? 'bg-yellow-500' : status === 'converted' ? 'bg-green-500' : status === 'lost' ? 'bg-red-500' : 'bg-gray-300'}`} />
                                    
                                    <div className="flex flex-1 items-center p-4 gap-4">
                                        {/* Checkbox */}
                                        <div 
                                            onClick={(e) => { e.stopPropagation(); const next = new Set(selectedIds); if (next.has(lead.id)) next.delete(lead.id); else next.add(lead.id); setSelectedIds(next); }}
                                            className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border transition-all ${isSelected ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300 group-hover:border-blue-400'}`}
                                        >
                                            {isSelected && <CheckCircle className="h-3.5 w-3.5 text-white" />}
                                        </div>

                                        {/* Content row */}
                                        <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                                            
                                            {/* Primary info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-0.5">
                                                    <h4 className="text-[15px] font-semibold text-gray-900 truncate pr-4">{lead.name}</h4>
                                                    <span className="text-[11px] font-medium text-gray-400 flex-shrink-0 sm:hidden whitespace-nowrap">{formatTimeAgo(lead.created_at)}</span>
                                                </div>
                                                <p className="text-sm text-gray-500 truncate">{lead.subject || lead.message}</p>
                                            </div>

                                            {/* Secondary info & Status */}
                                            <div className="flex items-center gap-3 sm:w-48 sm:flex-shrink-0 sm:justify-end">
                                                <div className="hidden sm:flex flex-col items-end mr-2">
                                                    <span className="text-[11px] font-medium text-gray-400">{formatDate(lead.created_at)}</span>
                                                    <span className="text-xs text-gray-500 truncate max-w-[120px]">{lead.email || lead.mobile_number}</span>
                                                </div>
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border whitespace-nowrap ${config.color.replace('border-', 'border-')}`}>
                                                    {config.label}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Right Panel: Detail View */}
            <div className={`w-full lg:w-[50%] xl:w-[40%] ${!selectedLeadId && 'hidden lg:block'} transition-all duration-300 sticky top-6 lg:h-[calc(100vh-160px)]`}>
                {!selectedLead ? (
                    <div className="bg-transparent border-2 border-dashed border-gray-200 rounded-3xl h-full hidden lg:flex flex-col items-center justify-center text-center p-12 min-h-[400px]">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                            <Eye className="h-6 w-6 text-gray-300" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-700">Select a lead</h3>
                        <p className="text-gray-400 text-sm mt-1 max-w-[200px]">Click on any lead in the list to view their detailed information here.</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 border border-gray-100 flex flex-col lg:h-full max-h-[calc(100vh-5rem)] overflow-hidden lg:animate-in lg:fade-in lg:slide-in-from-right-4 duration-300">
                        
                        {/* Mobile Back Button */}
                        <div className="lg:hidden p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                            <button onClick={() => setSelectedLeadId(null)} className="text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center gap-2">
                                <ChevronDown className="h-4 w-4 rotate-90" /> Back to list
                            </button>
                            <span className="text-xs font-semibold text-gray-400 tracking-wider">LEAD DETAILS</span>
                        </div>

                        {/* Details Header */}
                        <div className="p-6 sm:p-8 border-b border-gray-100 relative bg-white">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="flex items-center gap-3 mb-2">
                                        <h2 className="text-2xl font-bold text-gray-900">{selectedLead.name}</h2>
                                        {selectedLead.platform && (
                                            <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-bold uppercase rounded-md tracking-wide">
                                                {selectedLead.platform}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
                                        <a href={`mailto:${selectedLead.email}`} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600 transition-colors">
                                            <Mail className="h-4 w-4 text-gray-400" /> {selectedLead.email || 'No email'}
                                        </a>
                                        <a href={`tel:${selectedLead.mobile_number}`} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600 transition-colors">
                                            <Phone className="h-4 w-4 text-gray-400" /> {selectedLead.mobile_number}
                                        </a>
                                        {selectedLead.location && (
                                            <span className="flex items-center gap-1.5 text-sm text-gray-500">
                                                <div className="h-1.5 w-1.5 rounded-full bg-gray-300" /> {selectedLead.location}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Actions Row */}
                            <div className="flex items-center gap-3 mt-8">
                                <a href={toWhatsAppUrl(selectedLead.mobile_number)} target="_blank" rel="noreferrer" className="flex-1 flex justify-center items-center gap-2 px-4 py-2.5 bg-[#25D366] hover:bg-[#20BE5A] text-white text-sm font-semibold rounded-xl transition-all shadow-sm shadow-[#25D366]/20">
                                    <MessageSquare className="h-4 w-4" /> Message
                                </a>
                                <a href={`tel:${selectedLead.mobile_number}`} className="flex items-center justify-center p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl transition-all border border-gray-200">
                                    <Phone className="h-4 w-4" />
                                </a>
                                <button onClick={() => handleDeleteLead(selectedLead.id, selectedLead.name)} className="flex items-center justify-center p-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-all border border-red-100">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-8 bg-gray-50 lg:bg-transparent">
                            
                            {/* Status Section */}
                            <div>
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Current Status</h4>
                                <div className="grid grid-cols-2 gap-2">
                                    {['contacted', 'follow_up', 'converted', 'lost'].map((s) => {
                                        const c = statusConfig[s];
                                        const isCurrent = (leadStatuses[selectedLead.id] ?? getLeadStatus(selectedLead)) === s;
                                        return (
                                            <button 
                                                key={s}
                                                onClick={() => updateLeadStatus(selectedLead.id, s as LeadStatus)}
                                                className={`flex items-center gap-2 p-3 rounded-xl border transition-all text-sm font-medium whitespace-nowrap
                                                    ${isCurrent ? `${c.color} ring-2 ring-offset-1 ring-${c.iconColor.replace('text-', '')}/40` : `bg-white border-gray-200 text-gray-600 hover:border-gray-300`}
                                                `}
                                            >
                                                <c.icon className={`h-4 w-4 ${isCurrent ? c.iconColor : 'text-gray-400'}`} />
                                                {c.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Message Details */}
                            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-md uppercase tracking-wider">
                                        Subject
                                    </span>
                                    <h4 className="text-base font-semibold text-gray-900">{selectedLead.subject}</h4>
                                </div>
                                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50 p-4 rounded-xl border border-gray-100 font-medium">
                                    {selectedLead.message}
                                </div>
                                <div className="mt-4 flex items-center justify-between text-xs text-gray-400 font-medium pt-4 border-t border-gray-50">
                                    <span>Received {formatDate(selectedLead.created_at)}</span>
                                    <span>VIA {selectedLead.platform || 'Direct Contact'}</span>
                                </div>
                            </div>

                        </div>
                    </div>
                )}
            </div>
        </div>

      </div>

      {importModalOpen && (
        <ImportModal
          csvHeaders={importHeaders}
          csvRows={importRows}
          onConfirm={handleImportConfirm}
          onCancel={() => {
            setImportModalOpen(false); setImportHeaders([]); setImportRows([]);
          }}
          importing={importing}
        />
      )}
    </div>
  );
};

export default Leads;
